class HHVariantPicker extends HTMLElement {
  constructor() {
    super()
    this.addEventListener('change', this.onVariantChange)
  }

  shouldShowAtcPrice() {
    const productMain = document.querySelector('.product.main-product')
    return productMain?.dataset.showAtcPrice !== 'false'
  }

  getAddToCartLabel(price) {
    if (!this.shouldShowAtcPrice() || !price) return window.langMessages['products.product.add_to_cart']
    return `${window.langMessages['products.product.add_to_cart']} - ${price.replace('.00', '')}`
  }

  parsePriceValue(price) {
    if (!price) return null
    const parsedPrice = Number.parseFloat(String(price).replace(/[^0-9.-]/g, ''))
    return Number.isFinite(parsedPrice) ? parsedPrice : null
  }

  formatPrice(amount) {
    const currency = window.Shopify?.currency?.active || 'USD'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount)
  }

  // Shopify server-renders the add-to-cart button's data-price with the shop's own money
  // format (e.g. "Afl. 30"), which is always correct for the active market — unlike
  // Intl.NumberFormat's narrowSymbol, which falls back to the raw ISO code (e.g. "AWG")
  // for currencies it has no symbol glyph for. Reuse that notation instead of Intl's.
  getServerFormattedPriceSample() {
    return document.querySelector('[id*="product-form-"]')?.querySelector('[name="add"]')?.dataset?.price || null
  }

  formatAmountWithNotation(amount) {
    const sample = this.getServerFormattedPriceSample()
    if (!sample) return null
    const numberText = amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    const prefix = sample.match(/^[^\d]*/)?.[0] ?? ''
    const suffix = sample.match(/[^\d]*$/)?.[0] ?? ''
    return `${prefix}${numberText}${suffix}`
  }

  updateDiscountPercentage(priceElement, regularPrice, salePrice) {
    const priceWrapper = priceElement?.closest('.price')
    if (!priceWrapper) return

    const regularPriceValue = this.parsePriceValue(regularPrice)
    const salePriceValue = this.parsePriceValue(salePrice)
    const discountValue = (
      regularPriceValue && salePriceValue && regularPriceValue > salePriceValue
    )
      ? Math.round(((regularPriceValue - salePriceValue) / regularPriceValue) * 100)
      : 0

    const existingDiscount = priceWrapper.querySelector('.price__discount-percentage')

    if (discountValue <= 0) {
      existingDiscount?.remove()
      return
    }

    const discountContainer = existingDiscount || document.createElement('div')
    if (!existingDiscount) {
      discountContainer.classList.add('price__discount-percentage')
      const discountText = document.createElement('dd')
      discountContainer.appendChild(discountText)
      priceWrapper.appendChild(discountContainer)
    }

    const discountText = discountContainer.querySelector('dd')
    if (discountText) discountText.textContent = `(${discountValue}% OFF)`
  }

  onVariantChange() {
    this.updateOptions()
    this.updateMasterId()
    this.toggleAddButton(true, '', false)
    this.updatePickupAvailability()
    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true)
      this.setUnavailable()
    } else {
      this.updateMedia()
      this.scrollHHMediaSlider()
      this.updateURL()
      this.updateVariantInput()
      this.renderProductInfo()
    }
  }

  scrollHHMediaSlider() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return

    let mainSlider = document.querySelector('hh-image-carousel.product-media__sticky')
    if (!!!mainSlider) return;

    let variantImage = this.currentVariant?.featured_media?.preview_image.src
    let allSlides = mainSlider.querySelectorAll('.slide.product-media')

    if (!!!variantImage || !!!allSlides) return;

    let variantSlideIndex = [...allSlides].findIndex(slide => slide.dataset.url === variantImage)

    mainSlider.moveTo(variantSlideIndex)
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('.sp-product-variant-size, .vp__input--button, .js-dropdown-input'))
    if (fieldsets && fieldsets.length) {
      this.options = fieldsets.map((fieldset) => {
        if (fieldset.classList.contains('sp-product-variant-size')) {
          return Array.from(fieldset.querySelectorAll('input')).find(
            (radio) => radio.checked
          ).value
        } else if (fieldset.classList.contains('vp__input--button')) {
          return Array.from(fieldset.querySelectorAll('input')).find(
            (radio) => radio.checked
          ).value
        } else if (fieldset.classList.contains('js-dropdown-input')) {
          return fieldset.querySelector('input')?.value
        }
      })
    }
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] == option
        })
        .includes(false)
    })
    window.variantPrice = this.currentVariant.price / 100
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return
    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    )
    if (!newMedia) return
    const parent = newMedia.parentElement
    parent.prepend(newMedia)
    window.setTimeout(() => {
      parent.scroll(0, 0)
    })
  }

  updateURL() {
    if (!this.currentVariant) return
    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}?variant=${this.currentVariant.id}`
    )
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    )
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]')
      input.value = this.currentVariant.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
      const inputUrl = productForm.querySelector('input[name="sections_url"]')
      inputUrl.value = `${this.dataset.url}?variant=${this.currentVariant.id}`
      inputUrl.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability')
    if (!pickUpAvailability) return

    if (this.currentVariant?.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id)
    } else {
      pickUpAvailability.removeAttribute('available')
      pickUpAvailability.innerHTML = ''
    }
  }

  renderProductInfo() {
    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html')

        // Price: optional mobile + desktop duplicates (data-pdp-price)
        const priceAttr = `[data-pdp-price="${this.dataset.section}"]`
        const priceSources = html.querySelectorAll(priceAttr)
        const firstPriceSource = priceSources[0]
        const priceDestinations = document.querySelectorAll(priceAttr)

        const cartNotificationId = `cart-notification-product--${this.dataset.section}`
        const cartNotificationDestination = document.querySelector(`[data-section-id="${cartNotificationId}"]`)
        const cartNotificationSource = html.querySelector(`[data-section-id="${cartNotificationId}"]`)

        const errorNotificationId = `cart-notification-product-error-${this.dataset.section}`
        const errorNotificatioDestination = document.querySelector(`[data-section-id="${errorNotificationId}"]`)
        const errorNotificatioSource = html.querySelector(`[data-section-id="${errorNotificationId}"]`)

        const errorMessageId = `js-product-form__error-message--${this.dataset.section}`
        const errorMessageDestination = document.getElementById(errorMessageId)
        const errorMessageSource = html.getElementById(errorMessageId)

        const quantityButtonId = `quantity-input-${this.dataset.section}`
        const quantityButtonDestination = document.getElementById(quantityButtonId)
        const quantityButtonSource = html.getElementById(quantityButtonId)

        const atcFormId = `atc-form-${this.dataset.section}`
        const atcFormDestination = document.getElementById(atcFormId)
        const atcFormSource = html.getElementById(atcFormId)

        if (firstPriceSource && priceDestinations.length) {
          priceDestinations.forEach((destination) => {
            destination.innerHTML = firstPriceSource.innerHTML
          })
        }

        if (cartNotificationSource && cartNotificationDestination) cartNotificationDestination.innerHTML = cartNotificationSource.innerHTML

        if (errorNotificatioSource && errorNotificatioDestination) errorNotificatioDestination.innerHTML = errorNotificatioSource.innerHTML

        if (errorMessageSource && errorMessageDestination) errorMessageDestination.innerHTML = errorMessageSource.innerHTML

        if (quantityButtonSource && quantityButtonDestination) quantityButtonDestination.innerHTML = quantityButtonSource.innerHTML

        if (atcFormSource && atcFormDestination) atcFormDestination.dataset.itemid = atcFormSource.dataset.itemid //Needed to cart notification price update

        if (atcFormSource && atcFormDestination) atcFormDestination.querySelector('[name=quantity]').dataset.product = atcFormSource.querySelector('[name=quantity]').dataset.product

        document.querySelectorAll(priceAttr).forEach((node) => {
          node.classList.remove('visibility-hidden')
        })
        this.toggleAddButton(
          !this.currentVariant.available,
          window.langMessages['products.product.sold_out']
        )

        // Subscription price: apply to every price root (mobile + desktop when duplicated)
        const subscriptionWidget = document.querySelector('recharge-subscription-widget')
        const isSubscriptionSelected = !!subscriptionWidget?.shadowRoot?.querySelector('.rc-purchase-option__subscription[rc-selected]')
        let subscriptionPrice
        let rcOriginalPrice

        if (isSubscriptionSelected) {
          subscriptionWidget?.shadowRoot?.querySelector('.rc-purchase-option[rc-selected] .rc-price:not(.strike-through)')?.childNodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE && /\d/.test(node.nodeValue)) {
              subscriptionPrice = node.nodeValue.replace(/\b[A-Z]{3}\b/g, '').trim()
            }
          })

          // Prefer the RC widget's own original price (same currency notation it used for
          // subscriptionPrice) over our locally-formatted one — Intl.NumberFormat's narrowSymbol
          // falls back to the raw ISO code (e.g. "AWG") for currencies it has no symbol for,
          // which would mismatch Recharge's own rendering (e.g. "Afl.") for that same currency.
          subscriptionWidget?.shadowRoot?.querySelector('.rc-purchase-option[rc-selected] .rc-price.strike-through')?.childNodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE && /\d/.test(node.nodeValue)) {
              rcOriginalPrice = node.nodeValue.replace(/\b[A-Z]{3}\b/g, '').trim().replace('.00', '')
            }
          })
        }

        const priceRootsForSub = document.querySelectorAll(priceAttr)

        if (!!subscriptionWidget && !!subscriptionPrice && isSubscriptionSelected) {
          priceRootsForSub.forEach((priceRoot) => {
            const overallPrice = priceRoot.querySelector(
              '.price div:not(.price__strikethrough):not(.price__discount-percentage) dd'
            )
            if (!overallPrice) return

            const originalPrice = priceRoot.querySelector('.price div.price__strikethrough dd')
            const customStrikethroughPrice = document.createElement('div')
            customStrikethroughPrice.classList.add('price__strikethrough')

            const formattedVariantPrice = rcOriginalPrice || this.formatAmountWithNotation(window.variantPrice) || this.formatPrice(window.variantPrice)
            overallPrice.textContent = subscriptionPrice.replace('.00', '')

            if (formattedVariantPrice != subscriptionPrice.replace('.00', '')) {
              if (originalPrice) {
                originalPrice.textContent = formattedVariantPrice
              } else {
                const priceElement = document.createElement('dd')
                priceElement.textContent = formattedVariantPrice
                customStrikethroughPrice.appendChild(priceElement)

                overallPrice.closest('.price')?.append(customStrikethroughPrice)
              }
            } else if (originalPrice) {
              originalPrice.remove()
            }

            this.updateDiscountPercentage(
              overallPrice,
              formattedVariantPrice,
              subscriptionPrice.replace('.00', '')
            )
          })
        }

      })
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]')

    if (!addButton) return

    if (disable) {
      addButton.setAttribute('disabled', true)
      if (text) addButton.textContent = text
    } else {

      addButton.removeAttribute('disabled')

      const subscriptionWidget = document.querySelector('recharge-subscription-widget')
      const isSubscriptionSelected = !!subscriptionWidget?.shadowRoot?.querySelector('.rc-purchase-option__subscription[rc-selected]')

      // Change price in ATC if we have the subscription price
      let subscriptionPrice;

      if (isSubscriptionSelected) {
        subscriptionWidget?.shadowRoot?.querySelector('.rc-purchase-option[rc-selected] .rc-price:not(.strike-through)')?.childNodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE && /\d/.test(node.nodeValue)) {
            subscriptionPrice = node.nodeValue.replace(/\b[A-Z]{3}\b/g, '').trim()
          }
        });
      }

      const atcPrice = (!!subscriptionWidget && !!subscriptionPrice && isSubscriptionSelected)
        ? subscriptionPrice
        : (this.formatAmountWithNotation(this.currentVariant.price / 100) || this.formatPrice(this.currentVariant.price / 100))
      addButton.textContent = this.getAddToCartLabel(atcPrice)

      window.variantPrice = this.currentVariant.price / 100
    }

    if (!modifyClass) return
  }

  setUnavailable() {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]')
    if (!addButton) return
    addButton.textContent = window.langMessages['products.product.unavailable']
    document
      .querySelectorAll(`[data-pdp-price="${this.dataset.section}"]`)
      .forEach((node) => node.classList.add('visibility-hidden'))
  }

  getVariantData() {
    this.variantData =
      this.variantData ||
      JSON.parse(this.querySelector('[type="application/json"]').textContent)
    return this.variantData
  }
}

customElements.define('hh-variant-picker', HHVariantPicker)
